import { Component, EventEmitter, inject, input, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { LookupItem, LookupValueMapper } from '../../models/lookup-item';
import { InitialAssessmentForm } from '../../data/initial-assessment-form';
import { SponsorDetails } from '../../data/sponsor-details';
import { FormTypes } from '../../constants/form-types.constants';
import { IPageDetails } from '../../data/page-details';
import { IFormComponent } from '../../models/i-form-component';
import { AssessmentService } from '../../services/assessment.service';
import { Person } from '../../data/person';
import { ILookup } from '../../data/Ilookup';
import { has } from 'lodash-es';
import { FormControlHelpers } from '../../../helpers/form-control.helpers';


@Component({
  selector: 'app-sponsor-details',
  templateUrl: './sponsor-details.component.html',
  styleUrls: ['./sponsor-details.component.scss']
})
export class SponsorDetailsComponent implements OnInit, IFormComponent{
  
  assessmentService = inject(AssessmentService);
  formBuilder = inject(NonNullableFormBuilder);
  
  // Expose form types to template
  readonly formTypes = FormTypes;
  
  @Input() selectedForm!: IPageDetails | null;
  @Input() enquiryType!: string;
  @Input() formSubmitted!: boolean;
  @Input() model!: InitialAssessmentForm;
  @Input() titles!: LookupItem[];
  @Input() employmentStatus!: LookupItem[];
  @Input() visaStatus!: LookupItem[];
  @Input() maritalStatus!: LookupItem[];
  @Input() relationTypes!: LookupItem[];
  @Input() parentRelationTypes!: LookupItem[];
  @Input() childRelationTypes!: LookupItem[];
  @Input() phoneTypes!: LookupItem[];
  @Input() countries!: LookupItem[];
  @Output() formReady = new EventEmitter<FormGroup>();


  sponsorForm = this.formBuilder.group({
    hasSponsor: [''],
    title: [null as ILookup | null],
    titleOther: [''],
    firstName: [''],
    surname: [''],
    passportCountry: [null as ILookup | null],
    dob: [''],
    email: ['', [Validators.email]],
    phoneType: [''],
    phoneCountryCode: [''],
    phoneAreaCode: [''],
    phoneNumber: [''],
    countryResidence: [null as ILookup | null],
    previouslySponsored: [''],
    howManyPreviously: [''],
    employmentStatus: [null as ILookup | null],
    visaStatus: [null as ILookup | null],
    maritalStatus: [null as ILookup | null],
    realtionToApplicant: [null as ILookup | null],
    hasPartner: [''],
    partnerName: [''],
    partnerSurname: [''],
    partnerDob: ['']
  });

  // this is the sponsor partner page
  isPartnerSponsoring(): boolean {
    return this.assessmentService.isPartner();
  }

  isVisit(): boolean {
    return this.assessmentService.isVisit();
  }


  shouldShowSponsorDetails(): boolean {
    // show for all forms except visit when hasSponsor is false
    return !this.assessmentService.isVisit() || (this.assessmentService.isVisit() && this.sponsorForm.controls.hasSponsor.value === 'true');
  }

  ageDifference(dob: Date | string): number | null {
    return Person.ageDifference(dob);
  }

  showRelationToApplicant(): boolean {
    // Don't show for parent/child or partner sponsor
    return (!(this.assessmentService.isParent() || 
    this.assessmentService.isChild()) || 
    (this.isVisit() && this.sponsorForm.controls.hasSponsor.value === 'true') ) 
    && !this.isPartnerSponsoring();
    
  }

  showChildRelation(): boolean {
    return this.assessmentService.isChild();
  }

  showParentRelation(): boolean {
    return this.assessmentService.isParent();
  }

  showPartnerFields(): boolean {
    return this.showHasPartner() && this.sponsorForm.controls.hasPartner.value === 'true';

  }

  showHasPartner(): boolean{
    if(!this.sponsorForm.controls.dob.value){
      return false;
    }

    const age = this.ageDifference(this.sponsorForm.controls.dob.value);
    return age !== null && age >= 18;
  }

    getNameApplicant(): string {
    if (this.model.applicantDetails) {
      return this.model.applicantDetails.firstName + ' ' + this.model.applicantDetails.surname;
    }
    return '';
  }


  getNameApplicantOwnership(): string {
    const appName = this.getNameApplicant();
    if(appName == '') return '';
    let lastChar = appName.trim().split('').pop();
    return lastChar == 's' ? appName + "'" : appName + "'s"
  }

  ngOnInit(): void {
    this.model = this.model || new InitialAssessmentForm();
    this.model.sponsorDetails = this.model.sponsorDetails || new SponsorDetails();
    this.applyValidators();
    this.sponsorForm.patchValue({
      ...this.model.sponsorDetails,
      countryResidence: LookupValueMapper.toLookupItem(this.model.sponsorDetails.countryResidence)
    });

    this.sponsorForm.valueChanges.subscribe(value => {
      this.model.sponsorDetails = {
      ...this.model.sponsorDetails!,
      ...value,
        countryResidence: LookupValueMapper.toLookupValue(value.countryResidence)
    };

});
    this.formReady.emit(this.sponsorForm);
  }

  get pageTitle(): string {
    return this.assessmentService.isPartner() ? 'Partner Sponsor Details' : 'Sponsor Details';
  }
       
  
private applyValidators(): void {

      const firstNameControl = this.sponsorForm.controls.firstName;
      const surnameControl = this.sponsorForm.controls.surname;
      const emailControl = this.sponsorForm.controls.email;
      const titleControl = this.sponsorForm.controls.title;
      const titleOtherControl = this.sponsorForm.controls.titleOther;
      const relationToApplicantControl = this.sponsorForm.controls.realtionToApplicant;
      const partnerNameControl = this.sponsorForm.controls.partnerName;
      const partnerSurnameControl = this.sponsorForm.controls.partnerSurname;
      const partnerDobControl = this.sponsorForm.controls.partnerDob;
      const hasSponsorControl = this.sponsorForm.controls.hasSponsor;
      const hasPartnerControl = this.sponsorForm.controls.hasPartner;

    if(this.isPartnerSponsoring()){
        FormControlHelpers.setRequired(firstNameControl);
        FormControlHelpers.setRequired(surnameControl);
        FormControlHelpers.clearValidators(partnerNameControl);
        FormControlHelpers.clearValidators(partnerSurnameControl);
        FormControlHelpers.clearValidators(partnerDobControl);
        FormControlHelpers.clearValidators(titleOtherControl);
        FormControlHelpers.clearValidators(titleControl);
        FormControlHelpers.clearValidators(emailControl);
        FormControlHelpers.clearValidators(relationToApplicantControl);
        FormControlHelpers.clearValidators(hasSponsorControl);
    }
    else{
      
      if (this.isVisit()) {
        FormControlHelpers.setRequired(hasSponsorControl);
      } else {
        FormControlHelpers.clearValidators(hasSponsorControl);
      }

      if(!this.isVisit()){
        FormControlHelpers.setRequired(firstNameControl);
        FormControlHelpers.setRequired(surnameControl);
        FormControlHelpers.setRequired(emailControl);
        FormControlHelpers.clearValidators(hasSponsorControl);
      }else{
      FormControlHelpers.clearValidators(firstNameControl);
      FormControlHelpers.clearValidators(surnameControl);
      FormControlHelpers.clearValidators(emailControl);
      FormControlHelpers.clearValidators(titleOtherControl);
      FormControlHelpers.clearValidators(titleControl);

    }

    hasSponsorControl.valueChanges.subscribe(value => {
      if(value === 'true' && this.isVisit()){
        FormControlHelpers.setRequired(firstNameControl);
        FormControlHelpers.setRequired(surnameControl);
        FormControlHelpers.setRequired(emailControl);   
        FormControlHelpers.setRequired(relationToApplicantControl);    
    }
    else{
      FormControlHelpers.clearValidators(firstNameControl);
      FormControlHelpers.clearValidators(surnameControl);
      FormControlHelpers.clearValidators(emailControl);
      FormControlHelpers.clearValidators(titleOtherControl);
      FormControlHelpers.clearValidators(titleControl);
      FormControlHelpers.clearValidators(relationToApplicantControl);
    }
    });

    titleControl.valueChanges.subscribe(value => {
      if(value?.value === 'Other'){
        FormControlHelpers.setRequired(titleOtherControl); 
      }
      else{
        FormControlHelpers.clearValidators(titleOtherControl);
      }
    });
   
    hasPartnerControl.valueChanges.subscribe(value => {
      if(value === 'true'){
        FormControlHelpers.setRequired(partnerNameControl);
        FormControlHelpers.setRequired(partnerSurnameControl);
        FormControlHelpers.setRequired(partnerDobControl);
      }
      else{
        FormControlHelpers.clearValidators(partnerNameControl);
        FormControlHelpers.clearValidators(partnerSurnameControl);
        FormControlHelpers.clearValidators(partnerDobControl);
      }});
    
        
    if (this.showRelationToApplicant()) {
      FormControlHelpers.setRequired(relationToApplicantControl);
    } else {
      FormControlHelpers.clearValidators(relationToApplicantControl);
    }
  }
   
   
    
}


isFormValid(): boolean {
    return this.sponsorForm.valid;
}
markFormAsTouched(): void {
    this.sponsorForm.markAllAsTouched();
  }
}
